/*
Description: "full note" variant of the WebPT 1.0 injector (app.webpt.com addSOAP.php).

Selected instead of WebPTInject.ts when the new_webpt_integration feature flag is
on (see DOMEvaluator). Same scrapers and fill closures as WebPTInject, plus:
  - getQuestions walks every SOAP tab (Subjective → Billing) in one pass instead
    of only the active one, so one Comprehend fills the whole note.
  - Yes/No radios that showDiv() a hidden block are opened for the scrape, so the
    fields behind them become questions, and when a fill writes into such a block
    the radio that opens it is clicked for the user.
  - The panel gets one flex++ question per tab, like Raintree (see "flex++"
    below), with recursive prune and native multi-select; answers map back onto
    the legacy fill closures.

WebPT notes (verified on a live Progress Note):
  - All five tab bodies (`.zpTabsWebptContent > .zpTab#<name>`) are in the DOM at
    load; inactive ones are `display: none`. Clicking a header anchor
    (`.zpTabsWebpt > .zpTab a`) switches synchronously, so the walk stays sync.
  - showDiv(id) removes `hidden` from #id and adds `inset-tests`; hideDiv(id)
    adds `hidden` back. Values inside a hidden block are kept.
*/
import * as WebPT from './WebPTInject.ts';
import {
  beginOverlay,
  endOverlay,
  pushToQueue,
  queueLength,
  queueStatusMessage,
  removeFromQueue,
  resetOverlay,
  setCurrentDetail
} from './BlurOverlay.ts';
import {
  fillPlanGroup,
  planCheckboxLabels,
  planGroupTree,
  scrapePlanGroups,
  type CheckDesc
} from './WebPTPlanTab.ts';

export * from './WebPTInject.ts';

const SOAP_TABS = ['Subjective', 'Objective', 'Assessment', 'Plan', 'Billing'];
const SHOW_DIV_RE = /showDiv\('([^']+)'\)/g;
const HIDE_DIV_RE = /hideDiv\('([^']+)'\)/g;

let answerChain: Promise<unknown> = Promise.resolve();
let lastQuestions: any[] | null = null;

// Lets the overlay text and the tab switch paint between sections.
const paintPause = () => new Promise((resolve) => setTimeout(resolve, 30));

function tabHeader(name: string): HTMLElement | null {
  return (
    [...document.querySelectorAll<HTMLElement>('.zpTabsWebpt > .zpTab')].find(
      (el) => el.textContent?.trim() === name
    ) ?? null
  );
}

function tabBody(name: string): HTMLElement | null {
  return (
    [
      ...document.querySelectorAll<HTMLElement>('.zpTabsWebptContent > .zpTab')
    ].find((el) => el.id === name) ?? null
  );
}

function activeTab(): string {
  return document.querySelector('.zpTabsActive')?.textContent?.trim() ?? '';
}

function activateTab(name: string): boolean {
  if (!name) return false;
  if (activeTab() === name) return true;
  const header = tabHeader(name);
  const link = header?.querySelector<HTMLElement>('a') ?? header;
  if (!link) return false;
  link.click();
  return activeTab() === name;
}

function availableTabs(): string[] {
  return SOAP_TABS.filter((name) => {
    const body = tabBody(name);
    return !!tabHeader(name) && !!body?.querySelector('input, select, textarea');
  });
}

function divIds(onclick: string, re: RegExp): string[] {
  return [...onclick.matchAll(re)].map((m) => m[1]);
}

type Gate = { radio: HTMLInputElement; shows: string[]; hides: string[] };

function findGates(root: ParentNode): Gate[] {
  const radios = [
    ...root.querySelectorAll<HTMLInputElement>(
      'input[type="radio"][onclick*="showDiv("]'
    )
  ].map((radio) => {
    const onclick = radio.getAttribute('onclick') || '';
    return {
      radio,
      shows: divIds(onclick, SHOW_DIV_RE),
      hides: divIds(onclick, HIDE_DIV_RE)
    };
  });
  // Plan's checkboxes have their own scraper/filler (WebPTPlanTab).
  if ((root as HTMLElement).id === 'Plan') return radios;
  // Checkboxes that toggleVisible('<own id>') open #div<id> (e.g. Medical
  // History conditions and their notes box).
  const boxes = [
    ...root.querySelectorAll<HTMLInputElement>(
      'input[type="checkbox"][onclick*="toggleVisible("]'
    )
  ]
    .filter(
      (cb) =>
        (cb.getAttribute('onclick') || '').includes(`toggleVisible('${cb.id}')`) &&
        document.getElementById(`div${cb.id}`)
    )
    .map((cb) => ({ radio: cb, shows: [`div${cb.id}`], hides: [] }));
  return [...radios, ...boxes];
}

/**
 * Open every hidden showDiv() block so the scrapers see its fields, mirroring
 * what showDiv does. Returns an undo that re-hides exactly what was opened.
 */
function revealGatedBlocks(root: ParentNode): () => void {
  const opened: { el: HTMLElement; addedInset: boolean }[] = [];
  // Nested gates live inside blocks opened in an earlier pass.
  for (let pass = 0; pass < 4; pass++) {
    let openedThisPass = 0;
    for (const gate of findGates(root)) {
      for (const id of gate.shows) {
        const el = document.getElementById(id);
        if (!el || !el.classList.contains('hidden')) continue;
        el.classList.remove('hidden');
        const addedInset = !el.classList.contains('inset-tests');
        if (addedInset) el.classList.add('inset-tests');
        opened.push({ el, addedInset });
        openedThisPass++;
      }
    }
    if (!openedThisPass) break;
  }
  return () => {
    for (const { el, addedInset } of opened.reverse()) {
      el.classList.add('hidden');
      if (addedInset) el.classList.remove('inset-tests');
    }
  };
}

function blockSignature(el: HTMLElement): string {
  return [...el.querySelectorAll<HTMLElement>('input, select, textarea')]
    .map((c) => {
      if (c instanceof HTMLSelectElement) {
        return [...c.selectedOptions].map((o) => o.value).join(',');
      }
      if (
        c instanceof HTMLInputElement &&
        (c.type === 'checkbox' || c.type === 'radio')
      ) {
        return c.checked ? '1' : '0';
      }
      return (c as HTMLInputElement).value ?? '';
    })
    .join('\u0001');
}

function snapshotGatedBlocks(root: ParentNode): Map<string, string> {
  const snap = new Map<string, string>();
  for (const gate of findGates(root)) {
    for (const id of gate.shows) {
      const el = document.getElementById(id);
      if (el && !snap.has(id)) snap.set(id, blockSignature(el));
    }
  }
  return snap;
}

/**
 * A fill that wrote into a hidden block only sticks if the radio opening it is
 * selected, so pick that radio for the user. Per radio group: keep the current
 * choice if it already shows every changed block, else click the option that
 * shows the most changed blocks and hides the fewest.
 */
function openChangedBlocks(root: ParentNode, before: Map<string, string>) {
  const changed = new Set<string>();
  for (const [id, sig] of before) {
    const el = document.getElementById(id);
    if (el && blockSignature(el) !== sig) changed.add(id);
  }
  if (!changed.size) return;

  const groups = new Map<string, Gate[]>();
  for (const gate of findGates(root)) {
    if (!gate.shows.some((id) => changed.has(id))) continue;
    const name = gate.radio.name || gate.radio.id;
    groups.set(name, [...(groups.get(name) ?? []), gate]);
  }

  // Outer blocks first so a nested radio is clickable when its turn comes.
  const ordered = [...groups.values()].sort(
    (a, b) => depth(a[0].radio) - depth(b[0].radio)
  );
  for (const gates of ordered) {
    const wanted = new Set(
      gates.flatMap((g) => g.shows.filter((id) => changed.has(id)))
    );
    const groupName = gates[0].radio.name;
    const groupRadios = groupName
      ? [
          ...document.querySelectorAll<HTMLInputElement>('input[type="radio"]')
        ].filter((r) => r.name === groupName)
      : [gates[0].radio];
    const checked = groupRadios.find((r) => r.checked);
    const checkedGate = gates.find((g) => g.radio === checked);
    if (checkedGate && [...wanted].every((id) => checkedGate.shows.includes(id))) {
      continue;
    }
    const score = (g: Gate) =>
      g.shows.filter((id) => wanted.has(id)).length * 10 -
      g.hides.filter((id) => wanted.has(id)).length;
    const best = [...gates].sort((a, b) => score(b) - score(a))[0];
    if (best && !best.radio.checked) best.radio.click();
  }
}

/* ------------------------------------------------------------------------ *
 * flex++ (LambdaAPI/FlexPlusPlus.md)
 *
 * Like Raintree, the panel gets one question per tab (Subjective, Objective,
 * Assessment, Plan, Billing). Each is a nested flex++ tree of everything the
 * legacy scrape found on that tab: Flex sections become branches, long answers
 * and choice fields become leaves, and the Goal List becomes a list. Inside a
 * section, fields behind a Yes/No reveal nest under that question (nested
 * reveals nest deeper), and Month/Day/Year, Time/AM-PM and Left/Right parts nest
 * under their field, so prune_early can drop whole untouched branches and
 * multi-choice fields become native arrays. The legacy questions (with their
 * fill closures) stay in questionInteractions; tabParts maps each top-level key
 * of a tab tree back to the legacy question that fills it.
 * ------------------------------------------------------------------------ */

const FLEXPP_TOGGLE_KEY = 'flex++';
const FLEXPP_MAX_DEPTH = 10;
// The backend maps these to null already (GPTDialog.__cleanResponse); guard anyway.
const FLEX_SENTINELS = new Set(['not discussed', '-99999', '99999']);
const PART_SUFFIX_RE = /^(Month|Day|Year|Time|AM\/PM|Left|Right|Endfeel)$/;
const PAIN_SCALE_RE = /^Pain Scale \S+$/;
const COUNTER_RE = /\s+\(\d+\)$/;

type FlexSection = {
  section: string;
  sectionKey: string;
  // JSON(path) -> legacy field key
  leaves: Map<string, string>;
  multi: Set<string>;
};
type TabPart =
  | { kind: 'flex'; question: string; section: FlexSection }
  | { kind: 'text' | 'choice' | 'multi'; question: string }
  | { kind: 'goals'; question: string }
  | { kind: 'plan'; group: Map<string, CheckDesc> };
// tab -> top-level key in its tree -> legacy question it fills
let tabParts: Record<string, Map<string, TabPart>> = {};

/**
 * Mirrors the backend's sanitize_key (drops quotes, trims) plus the label noise
 * WebPTInject's sanitizeQuestionKey strips from scraped keys.
 */
function cleanKey(s: string): string {
  return String(s)
    .split('\n')[0]
    .replace(/\s*\*?\s*This is a required field\s*/gi, ' ')
    .replace(/["']/g, '')
    .replace(/\s+/g, ' ')
    .replace(/:\s*$/, '')
    .trim();
}

const normLabel = (s: string) => cleanKey(s).toLowerCase();

function isBlankFlexValue(v: any): boolean {
  if (v === null || v === undefined) return true;
  const s = String(v).trim();
  return s === '' || FLEX_SENTINELS.has(s.toLowerCase());
}

type GateInfo = { label: string; parent: string | null };

/**
 * Yes/No question label -> the reveal block it sits in, if any. Labels are read
 * with innerText like the scraper, so call this while the tab is rendered
 * (hidden badges such as "MIPS" would otherwise leak into the label).
 */
function collectGates(
  root: ParentNode = document,
  into = new Map<string, GateInfo>()
): Map<string, GateInfo> {
  // Only Yes/No questions name a branch; checkbox gates sit inside a field.
  const gates = findGates(root).filter((g) => g.radio.type === 'radio');
  const labelOf = (radio: Element) => {
    const col = radio.closest('.rowdiv')?.querySelector<HTMLElement>('.col1div');
    return cleanKey(col?.innerText ?? col?.textContent ?? '');
  };
  const blockLabel = new Map<string, string>();
  for (const gate of gates) {
    const label = labelOf(gate.radio);
    if (!label) continue;
    for (const id of gate.shows) if (!blockLabel.has(id)) blockLabel.set(id, label);
  }
  const info = into;
  for (const gate of gates) {
    const label = labelOf(gate.radio);
    if (!label || info.has(normLabel(label))) continue;
    let parent: string | null = null;
    const row = gate.radio.closest('.rowdiv');
    for (let el = row?.parentElement; el; el = el.parentElement) {
      const outer = el.id && blockLabel.get(el.id);
      if (outer && normLabel(outer) !== normLabel(label)) {
        parent = outer;
        break;
      }
    }
    info.set(normLabel(label), { label, parent });
  }
  return info;
}

function gateChain(label: string, gates: Map<string, GateInfo>): string[] {
  const chain: string[] = [];
  const seen = new Set<string>();
  for (let cur: string | null = label; cur && !seen.has(normLabel(cur)); ) {
    seen.add(normLabel(cur));
    chain.unshift(cleanKey(cur));
    cur = gates.get(normLabel(cur))?.parent ?? null;
  }
  return chain;
}

/**
 * Legacy keys look like "<parent row>: - <label> - <part> (n)". The parent row
 * label (it keeps its colon) or a reveal question becomes a branch; a trailing
 * date/time/side part becomes a leaf under its field.
 */
function fieldPath(key: string, gates: Map<string, GateInfo>): string[] {
  const counter = key.match(COUNTER_RE)?.[0] ?? '';
  const segs = key
    .slice(0, key.length - counter.length)
    .split(' - ')
    .map((s) => s.trim())
    .filter(Boolean);
  const path: string[] = [];
  if (segs.length > 1 && gates.has(normLabel(segs[0]))) {
    path.push(...gateChain(segs.shift(), gates));
  } else if (segs.length > 1 && segs[0].endsWith(':')) {
    path.push(cleanKey(segs.shift()));
  }
  if (segs.length > 1 && PAIN_SCALE_RE.test(cleanKey(segs[0]))) {
    path.push(cleanKey(segs.shift()));
  }
  let leaf = segs.pop() ?? 'Details';
  if (/^Untitled$/i.test(leaf)) leaf = 'Details';
  const base = segs.join(' - ');
  if (base && PART_SUFFIX_RE.test(leaf)) path.push(cleanKey(base), leaf);
  else path.push(cleanKey(base ? `${base} - ${leaf}` : leaf));
  path[path.length - 1] += counter;
  if (path.length > FLEXPP_MAX_DEPTH - 1) {
    path.splice(FLEXPP_MAX_DEPTH - 2, Infinity, path.slice(FLEXPP_MAX_DEPTH - 2).join(' - '));
  }
  return path;
}

/** Paths for a section's keys; a name used as both leaf and branch keeps the leaf as "Answer". */
function assignPaths(keys: string[], gates: Map<string, GateInfo>): string[][] {
  const paths = keys.map((k) => fieldPath(k, gates));
  const branches = new Set<string>();
  paths.forEach((p) =>
    p.slice(0, -1).forEach((_, i) => branches.add(JSON.stringify(p.slice(0, i + 1))))
  );
  const used = new Set<string>();
  return paths.map((p) => {
    const base = branches.has(JSON.stringify(p)) ? [...p, 'Answer'] : p;
    let out = base;
    for (let n = 2; used.has(JSON.stringify(out)); n++) {
      out = [...base.slice(0, -1), `${base[base.length - 1]} (${n})`];
    }
    used.add(JSON.stringify(out));
    return out;
  });
}

function setAtPath(root: any, path: string[], value: any) {
  let node = root;
  for (const seg of path.slice(0, -1)) node = node[seg] ??= {};
  node[path[path.length - 1]] = value;
}

function uniqueStrings(values: any[]): string[] {
  return [...new Set(values.map((v) => String(v).trim()).filter(Boolean))];
}

function toFlexLeaf(field: any): Record<string, any> {
  const node: Record<string, any> = {};
  const existing = field['Existing Value'];
  const multi = WebPT.multiChoiceFields.get(field);
  if (multi?.length) {
    node._choices = uniqueStrings(multi);
    node._multiple = true;
    node._description = 'Every option that applies; empty list if none.';
    if (!isBlankFlexValue(existing)) {
      node._existingValue = String(existing).split('$').map((s) => s.trim()).join(', ');
    }
  } else {
    const choices = Array.isArray(field.Choices) ? uniqueStrings(field.Choices) : [];
    if (choices.length) node._choices = choices;
    if (!isBlankFlexValue(existing)) node._existingValue = String(existing);
    if (typeof field.Description === 'string' && field.Description) {
      node._description = field.Description;
    }
  }
  // A leaf needs at least one reserved key or the backend reads it as a branch.
  if (!Object.keys(node).length) node._type = 'string';
  return node;
}

/** flex++ Structure for one legacy Flex question, or null to leave it legacy. */
function toFlexPP(q: any, gates: Map<string, GateInfo>) {
  const legacy = q.Structure;
  if (q.Type !== 'Flex' || !legacy || typeof legacy !== 'object' || Array.isArray(legacy)) {
    return null;
  }
  const structure: Record<string, any> = {};
  const sections: FlexSection[] = [];
  for (const [section, fields] of Object.entries(legacy)) {
    if (!fields || typeof fields !== 'object' || Array.isArray(fields)) return null;
    const entries = Object.entries(fields as Record<string, any>).filter(
      ([, f]) => f && typeof f === 'object'
    );
    if (!entries.length) continue;
    const sectionKey = cleanKey(section);
    const tree: Record<string, any> = {};
    const leaves = new Map<string, string>();
    const multi = new Set<string>();
    const paths = assignPaths(entries.map(([k]) => k), gates);
    entries.forEach(([key, field], i) => {
      const id = JSON.stringify(paths[i]);
      setAtPath(tree, paths[i], toFlexLeaf(field));
      leaves.set(id, key);
      if (WebPT.multiChoiceFields.has(field)) multi.add(id);
    });
    for (const path of paths) {
      let node = tree;
      for (const seg of path.slice(0, -1)) {
        node = node[seg];
        const gate = gates.get(normLabel(seg));
        if (gate && !node._description) {
          node._description = `Only shown in WebPT once "${gate.label}" is answered to open it. Fill only what the visit covered; otherwise leave it empty.`;
        }
      }
    }
    structure[sectionKey] = tree;
    sections.push({ section, sectionKey, leaves, multi });
  }
  if (!sections.length) return null;
  return { structure, sections };
}

function toLegacyValue(value: any, isMulti: boolean): string | undefined {
  if (Array.isArray(value)) {
    const items = value.filter((v) => !isBlankFlexValue(v)).map(String);
    return items.length ? items.join('$') : undefined;
  }
  if (isBlankFlexValue(value) || typeof value === 'object') return undefined;
  // Undo hands back the ", "-joined _existingValue of a multi-choice leaf.
  if (isMulti) return String(value).split(/\s*[,$]\s*/).filter(Boolean).join('$');
  return String(value);
}

/** Nested flex++ section answer -> legacy {fieldKey: value} for answerFlexQuestion. */
function sectionAnswerToLegacy(section: FlexSection, root: any) {
  const fields: Record<string, string> = {};
  const walk = (node: any, path: string[]) => {
    for (const [k, v] of Object.entries(node)) {
      if (k.startsWith('_')) continue;
      const p = [...path, cleanKey(k)];
      const id = JSON.stringify(p);
      const legacyKey = section.leaves.get(id);
      if (legacyKey) {
        const val = toLegacyValue(v, section.multi.has(id));
        if (val !== undefined) fields[legacyKey] = val;
      } else if (v && typeof v === 'object' && !Array.isArray(v)) {
        walk(v, p);
      }
    }
  };
  if (root && typeof root === 'object' && !Array.isArray(root)) walk(root, []);
  return fields;
}

/** {"0": x, "1": y} -> [x, y]; the backend may send lists as indexed dicts. */
function asList(value: any): any[] | null {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') {
    const keys = Object.keys(value);
    if (keys.length && keys.every((k) => /^\d+$/.test(k))) {
      return keys.sort((a, b) => +a - +b).map((k) => value[k]);
    }
  }
  return null;
}

function textLeaf(q: any): Record<string, any> {
  const node: Record<string, any> = {};
  if (!isBlankFlexValue(q.ExistingNote)) node._existingValue = String(q.ExistingNote);
  // Some keys are shortened from the on-screen prompt; give the AI the prompt.
  const prompt = cleanKey(q.Question);
  if (prompt && prompt !== cleanKey(q.Key)) node._description = prompt;
  if (!Object.keys(node).length) node._type = 'string';
  return node;
}

/** One flex++ question holding everything the legacy scrape found on a tab. */
function buildTabQuestion(tab: string, qs: any[], gates: Map<string, GateInfo>) {
  const tree: Record<string, any> = {
    _description: `Everything on the WebPT ${tab} tab of this note.`,
    // Pruning the tab root would drop the whole question.
    _no_prune: true
  };
  const parts = new Map<string, TabPart>();
  const used = new Set<string>();
  const uniqueKey = (raw: string) => {
    const base = cleanKey(raw) || 'Details';
    let key = base;
    for (let n = 2; used.has(key); n++) key = `${base} (${n})`;
    used.add(key);
    return key;
  };

  // Plan's checkbox groups are scraped from the DOM (with what opens under each
  // item) instead of the legacy flat Choose All lists.
  const planGroups = tab === 'Plan' ? scrapePlanGroups(tabBody(tab) ?? document) : new Map();
  const planLabels = planCheckboxLabels(planGroups);

  for (const q of qs) {
    // isWebPTUser's " - Flex" twins of the Objective long answers stay skipped.
    if (q.ReplyType === 'no_reply') continue;
    if (q.Type === 'Choose All' && planGroups.has(cleanKey(q.Key))) continue;
    const choices = Array.isArray(q.Choices) ? uniqueStrings(q.Choices) : [];

    if (q.Type === 'Flex' && Array.isArray(q.Structure)) {
      if (!q.Structure.length) continue;
      const key = uniqueKey(q.Key);
      tree[key] = q.Structure.map((goal: any) =>
        Object.fromEntries(
          Object.entries(goal || {}).map(([k, f]) => [cleanKey(k), toFlexLeaf(f)])
        )
      );
      parts.set(key, { kind: 'goals', question: q.Question });
    } else if (q.Type === 'Flex') {
      const flex = toFlexPP(q, gates);
      if (!flex) continue;
      for (const section of flex.sections) {
        const sectionTree = flex.structure[section.sectionKey];
        // The legacy Plan scrape reads nested treatment checkboxes as fields.
        for (const [id, legacyKey] of [...section.leaves]) {
          if (!planLabels.has(cleanKey(legacyKey))) continue;
          const path: string[] = JSON.parse(id);
          let node = sectionTree;
          for (const seg of path.slice(0, -1)) node = node?.[seg];
          if (node) delete node[path[path.length - 1]];
          section.leaves.delete(id);
        }
        if (!section.leaves.size) continue;
        const name =
          tab === 'Plan' && section.sectionKey === 'Untitled Section'
            ? 'Plan of Care'
            : section.sectionKey;
        const key = uniqueKey(name);
        tree[key] = sectionTree;
        parts.set(key, { kind: 'flex', question: q.Question, section });
      }
    } else if (q.Type === 'Choose All' && choices.length) {
      const key = uniqueKey(q.Key);
      tree[key] = { _choices: choices, _multiple: true };
      parts.set(key, { kind: 'multi', question: q.Question });
    } else if (q.Type === 'Multiple Choice' && choices.length) {
      const key = uniqueKey(q.Key);
      tree[key] = { _choices: choices };
      parts.set(key, { kind: 'choice', question: q.Question });
    } else {
      const key = uniqueKey(q.Key);
      tree[key] = textLeaf(q);
      parts.set(key, { kind: 'text', question: q.Question });
    }
  }
  planGroups.forEach((group, name) => {
    const key = uniqueKey(name);
    tree[key] = planGroupTree(group, name);
    parts.set(key, { kind: 'plan', group });
  });

  if (!parts.size) return null;
  return {
    question: {
      Question: tab,
      Key: tab,
      Type: 'Flex',
      Choices: undefined,
      ExistingNote: null,
      Structure: { [tab]: tree, [FLEXPP_TOGGLE_KEY]: true, prune_early: true },
      ReplyType: null
    },
    parts
  };
}

function depth(el: Element): number {
  let d = 0;
  for (let p = el.parentElement; p; p = p.parentElement) d++;
  return d;
}

export function getQuestions(isWebPTUser = false) {
  // Mid-fill re-scrapes would click tabs under the fill; reuse the last scan.
  if (queueLength() > 0 && lastQuestions) return lastQuestions;
  // Nothing queued means no fill is running; drop anything an interrupted one left.
  if (queueLength() === 0) resetOverlay();
  const tabs = availableTabs();
  if (tabs.length < 2) {
    tabParts = {};
    return WebPT.getQuestions(isWebPTUser);
  }

  const originalTab = activeTab();
  const merged: Record<string, any> = {};
  const byTab: Record<string, any[]> = {};
  const seenKeys = new Set<string>();
  const gates = new Map<string, GateInfo>();
  tabParts = {};

  for (const tab of tabs) {
    if (!activateTab(tab)) {
      console.warn('[WebPT] could not open tab', tab);
      continue;
    }
    const restore = revealGatedBlocks(tabBody(tab) ?? document);
    let tabQuestions: any[] = [];
    try {
      tabQuestions = WebPT.getQuestions(isWebPTUser) || [];
    } catch (err) {
      // One tab's legacy scrape failing shouldn't sink the whole note.
      console.warn('[WebPT] scrape failed on tab', tab, err);
    }
    try {
      collectGates(tabBody(tab) ?? document, gates);
    } finally {
      restore();
    }
    const interactions = WebPT.getQuestionInteractions();
    byTab[tab] = [];
    for (const q of tabQuestions) {
      // Some branches (Objective → Billing fallback) re-scrape another tab's
      // fields under the same keys; the first tab that owns a key wins.
      if (merged[q.Question] || seenKeys.has(q.Key)) continue;
      merged[q.Question] = interactions[q.Question];
      seenKeys.add(q.Key);
      byTab[tab].push(q);
    }
  }

  activateTab(originalTab);
  WebPT.setQuestionInteractions(merged);

  const questions = [];
  for (const tab of tabs) {
    const built = byTab[tab] && buildTabQuestion(tab, byTab[tab], gates);
    if (!built) continue;
    tabParts[tab] = built.parts;
    questions.push(built.question);
  }
  console.log('[WebPT] full-note tab questions', questions.map((q) => q.Key));
  lastQuestions = questions;
  return questions;
}

/**
 * Capture keeps the legacy active-tab scrape (no clicks) but shapes it like that
 * tab's question, so stored notes line up with what Comprehend wrote.
 */
export function getQuestionsForCapture(): Record<string, any> {
  const values = WebPT.getQuestionsForCapture();
  const tab = activeTab();
  if (!SOAP_TABS.includes(tab)) return values;
  const gates = collectGates(tabBody(tab) ?? document);
  const tree: Record<string, any> = {};
  const used = new Set<string>();
  const uniqueKey = (raw: string) => {
    const base = cleanKey(raw) || 'Details';
    let key = base;
    for (let n = 2; used.has(key); n++) key = `${base} (${n})`;
    used.add(key);
    return key;
  };
  for (const [key, value] of Object.entries(values)) {
    if (key.endsWith(' - Flex')) continue;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      for (const [section, fields] of Object.entries(value as Record<string, any>)) {
        if (!fields || typeof fields !== 'object') continue;
        const keys = Object.keys(fields);
        const nested = {};
        assignPaths(keys, gates).forEach((p, i) => setAtPath(nested, p, fields[keys[i]]));
        tree[uniqueKey(section)] = nested;
      }
    } else {
      tree[uniqueKey(key)] = value;
    }
  }
  return Object.keys(tree).length ? { [tab]: { [tab]: tree } } : {};
}

async function answerTab(tab: string, answer: any, replyType) {
  const parts = tabParts[tab];
  const rootKey = Object.keys(answer || {}).find((k) => cleanKey(k) === tab);
  const root = rootKey ? answer[rootKey] : answer;
  if (!root || typeof root !== 'object') return 'Answer not found';

  const errors: any[] = [];
  const run = async (question: string, value: any) => {
    const err = await WebPT.answerQuestion(question, value, replyType);
    if (err && err !== true) errors.push({ Question: question, Error: err });
  };

  // One section at a time, like Raintree / WebPT 2.0, with the overlay naming it.
  for (const [k, v] of Object.entries(root)) {
    if (k.startsWith('_')) continue;
    const part = parts.get(cleanKey(k));
    if (!part) continue;
    setCurrentDetail(cleanKey(k));
    await paintPause();
    if (part.kind === 'plan') {
      fillPlanGroup(part.group, v, replyType);
    } else if (part.kind === 'flex') {
      const fields = sectionAnswerToLegacy(part.section, v);
      if (Object.keys(fields).length) {
        await run(part.question, { [part.section.section]: fields });
      }
    } else if (part.kind === 'multi') {
      const picks = (asList(v) ?? String(v ?? '').split(/\s*[,$]\s*/)).filter(
        (x) => !isBlankFlexValue(x)
      );
      if (picks.length) await run(part.question, picks.map(String));
    } else if (part.kind === 'goals') {
      const items = (asList(v) ?? [])
        .map((goal) =>
          Object.fromEntries(
            Object.entries(goal || {})
              .map(([f, val]) => [f, toLegacyValue(val, false)])
              .filter(([, val]) => val !== undefined)
          )
        );
      if (!items.some((g) => Object.keys(g).length)) continue;
      // answerFlexQuestion sizes the goal rows off the array and fills from .answer.
      await run(part.question, Object.assign([...items], { answer: items }));
    } else {
      const val = toLegacyValue(v, false);
      if (val !== undefined) await run(part.question, val);
    }
  }
  return errors.length ? errors : null;
}

async function answerOnTab(question, answer, replyType) {
  const tab = tabParts[question] ? question : null;
  const originalTab = activeTab();
  if (tab) activateTab(tab);
  const root = (tab && tabBody(tab)) || document;
  const before = snapshotGatedBlocks(root);
  try {
    return tab
      ? await answerTab(tab, answer, replyType)
      : await WebPT.answerQuestion(question, answer, replyType);
  } finally {
    openChangedBlocks(root, before);
    if (originalTab) activateTab(originalTab);
  }
}

export function answerQuestion(question, answer, replyType) {
  // The panel fires answers concurrently; each one switches tabs, so run them
  // one at a time. Queue it now so the overlay lists what's coming.
  const label = String(question);
  pushToQueue(label);
  beginOverlay(queueStatusMessage());
  const run = answerChain
    .then(() => answerOnTab(question, answer, replyType))
    .finally(() => {
      removeFromQueue(label);
      endOverlay();
    });
  answerChain = run.catch(() => undefined);
  return run;
}

export async function answerQuestions(answers) {
  const errors = [];
  for (const answer of answers) {
    const error = await answerQuestion(
      answer['Question'],
      answer['Answer'],
      answer['Reply Type']
    );
    if (error && error !== true) {
      errors.push({
        Question: answer['Question'],
        Answer: answer['Answer'],
        Error: error
      });
    }
  }
  return errors;
}
